import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useState, useEffect } from "react";
import { axiosInstance } from "../../../utils/axiosInstance";
import { Location } from "../../../interfaces/interfaces";
import LocationFormFields, {
  EMPTY_LOCATION_FORM,
  LocationFormValues,
  locationToForm,
} from "./LocationFormFields";

interface EditLocationModalProps {
  open: boolean;
  location: Location;
  onClose: () => void;
  onSaveSuccess: (message: string) => void;
}

export default function EditLocationModal({
  open,
  location,
  onClose,
  onSaveSuccess,
}: EditLocationModalProps) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [syncingMachines, setSyncingMachines] = useState<boolean>(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [values, setValues] = useState<LocationFormValues>(EMPTY_LOCATION_FORM);

  const patch = (next: Partial<LocationFormValues>) =>
    setValues((prev) => ({ ...prev, ...next }));

  // Populate form when location changes
  useEffect(() => {
    if (location) {
      setValues(locationToForm(location));
      setErrors({});
      setSyncMessage(null);
    }
  }, [location]);

  const { layoutId, apiId } = values;

  const canSyncMachines =
    Boolean(layoutId?.trim()) && Boolean(apiId?.trim()) && !syncingMachines;

  const handleSyncMachines = async () => {
    if (!layoutId?.trim() || !apiId?.trim()) {
      setErrors({
        general: "Enter both Layout ID and API ID before syncing machines.",
      });
      return;
    }

    setSyncingMachines(true);
    setSyncMessage(null);
    setErrors({});

    try {
      const res = await axiosInstance.post(
        `/location/${location.locationId}/sync-machines`,
        {
          layoutId: layoutId.trim(),
          apiId: apiId.trim(),
        }
      );
      const data = res.data?.data;
      const detail = data
        ? `Fetched ${data.fetched}: ${data.liveStatusAdded} added, ${data.liveStatusUpdated} updated in live status.`
        : "";
      setSyncMessage(
        `${res.data?.message || "Machines synced successfully"}${
          detail ? ` ${detail}` : ""
        }`
      );
    } catch (error: any) {
      console.error("Error syncing machines:", error);
      setErrors({
        general:
          error.response?.data?.message ||
          "Failed to sync machines from Alliance",
      });
    } finally {
      setSyncingMachines(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setErrors({});

    const updateData: any = {
      name: values.name,
      locationPin: values.locationPin,
      address: values.address,
      postcode: values.postcode,
      latitude: values.latitude || undefined,
      longitude: values.longitude || undefined,
      email: values.email,
      phone: values.phone,
      open: values.is24 ? "" : values.open || "",
      close: values.is24 ? "" : values.close || "",
      hasPawwash: values.hasPawwash.toString(),
      is24: values.is24.toString(),
      siteStatus: values.siteStatus,
      dry: values.dry,
      pet8kg: values.pet8kg,
      pet14kg: values.pet14kg,
      petdry: values.petdry,
      wash8kg: values.wash8kg,
      wash14kg: values.wash14kg,
      wash18kg: values.wash18kg,
    };

    // Only include optional fields if they have values
    if (values.layoutLink) updateData.layoutLink = values.layoutLink;
    if (values.layoutId) updateData.layoutId = values.layoutId.toString();
    if (values.apiId) updateData.apiId = values.apiId;
    if (values.reviewlink) updateData.reviewlink = values.reviewlink;

    const nitroTrim = values.nitroCmmsLocationId.trim();
    if (nitroTrim === "") {
      updateData.nitro_cmms_location_id = null;
    } else {
      const n = Number(nitroTrim);
      if (!Number.isFinite(n) || n < 1) {
        setErrors({ nitro_cmms_location_id: "Must be a positive integer" });
        setLoading(false);
        return;
      }
      updateData.nitro_cmms_location_id = n;
    }

    try {
      const res = await axiosInstance.put(
        `/location/${location.locationId}`,
        updateData
      );

      if (res.data.success === false && res?.data?.validation === false) {
        const validationErrors: Record<string, string> = {};
        res?.data.message.forEach((element: any) => {
          validationErrors[element.context.key] = element.message;
        });
        setErrors(validationErrors);
        setLoading(false);
        return;
      } else if (res.data.success === true || res.status === 200) {
        // If alerts state changed, update it separately via PATCH endpoint
        if (values.alerts !== location.alerts_enabled) {
          try {
            await axiosInstance.patch(`/location/${location.locationId}/alerts`, {
              alerts_enabled: values.alerts,
            });
          } catch (alertError: any) {
            console.error("Error updating alerts:", alertError);
            // Don't fail the whole save if alerts update fails - just log it
            if (alertError.response?.status !== 401) {
              // Only show error if it's not an auth error (which will redirect)
              setErrors({ alerts: "Failed to update alerts setting" });
            }
          }
        }
        onSaveSuccess("Location updated successfully");
      }
    } catch (error: any) {
      console.error("Error updating location:", error);
      setErrors({
        general: error.response?.data?.message || "Failed to save the location.",
      });
    }
    setLoading(false);
  };

  const hasFieldErrors = Object.keys(errors).some((k) => k !== "general");

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Edit {location.name}
        <IconButton aria-label="Close" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {errors.general ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.general}
          </Alert>
        ) : null}
        {hasFieldErrors ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            Check the highlighted fields.
          </Alert>
        ) : null}
        <LocationFormFields
          mode="edit"
          values={values}
          onChange={patch}
          errors={errors}
          disabled={loading}
          integrationExtra={
            <div className="sq-sync">
              <Button
                variant="outlined"
                onClick={handleSyncMachines}
                disabled={!canSyncMachines || loading}
              >
                {syncingMachines ? "Syncing machines…" : "Sync machines"}
              </Button>
              <p className="sq-muted mb-0">
                Pulls Alliance machines into live status after Layout ID and API ID are set.
              </p>
              {syncMessage ? (
                <Alert severity="success" sx={{ width: "100%" }}>
                  {syncMessage}
                </Alert>
              ) : null}
            </div>
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={loading}>
          {loading ? "Saving…" : "Save changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
